import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  CalendarIcon, 
  BoltIcon, 
  ShieldCheckIcon, 
  MapPinIcon, 
  QrCodeIcon, 
  Cog6ToothIcon, 
  UserIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  PauseIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

interface Slide {
  id: number;
  title: string;
  description: string;
  highlight: string;
  icon: React.ReactNode;
  color: string;
  gradient: string;
  imageUrl: string;
  cta?: string;
}

const AppScreenshotSlider: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize the slides array to prevent unnecessary re-renders
  const slides = useMemo<Slide[]>(() => [
    {
      id: 1,
      title: "Smart Event Planning",
      description: "Transform how you organize events with intelligent planning tools and sustainable impact tracking.",
      highlight: "Plan events the smart way",
      icon: <CalendarIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen1.png",
      cta: "Get Started"
    },
    {
      id: 2,
      title: "Instant Setup",
      description: "Get started in seconds with phone verification and quick registration. No lengthy forms.",
      highlight: "30-second registration",
      icon: <BoltIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen2.png",
      cta: "Try Now"
    },
    {
      id: 3,
      title: "Secure Verification",
      description: "One-time secure verification ensures your account safety while keeping access simple.",
      highlight: "Verify once, stay connected",
      icon: <ShieldCheckIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen3.png",
      cta: "Secure Login"
    },
    {
      id: 4,
      title: "Centralized Management",
      description: "All your events in one place with detailed analytics, attendance tracking, and scheduling.",
      highlight: "Complete event dashboard",
      icon: <DevicePhoneMobileIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen4.png",
      cta: "View Dashboard"
    },
    {
      id: 5,
      title: "Quick Event Creation",
      description: "Create events in seconds with dates, locations, live streams, and detailed schedules.",
      highlight: "Add events in seconds",
      icon: <CalendarIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen5.png",
      cta: "Create Event"
    },
    {
      id: 6,
      title: "Smart Location Finder",
      description: "Find perfect event locations with smart search, map integration, and current location detection.",
      highlight: "Perfect event locations",
      icon: <MapPinIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen6.png",
      cta: "Find Location"
    },
    {
      id: 7,
      title: "QR Code Sharing",
      description: "Share events instantly with unique QR codes. Download, share links, or scan directly.",
      highlight: "Share events instantly",
      icon: <QrCodeIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen7.png",
      cta: "Generate QR"
    },
    {
      id: 8,
      title: "Custom Settings",
      description: "Control your experience with customizable preferences, notifications, and personal details.",
      highlight: "Your preferences, your way",
      icon: <Cog6ToothIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen8.png",
      cta: "Customize"
    },
    {
      id: 9,
      title: "Data Control",
      description: "Full control over your data with privacy-focused features including account deletion.",
      highlight: "Your data, your control",
      icon: <UserIcon className="w-8 h-8" />,
      color: "text-green-600",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
      imageUrl: "/screenshots/screen9.png",
      cta: "Learn More"
    }
  ], []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAutoPlaying) {
      interval = setInterval(nextSlide, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const currentSlideData = slides[currentSlide];

  // Preload images
  useEffect(() => {
    const imagePromises = slides.map((slide) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = slide.imageUrl;
        img.onload = resolve;
        img.onerror = reject;
      });
    });

    Promise.all(imagePromises)
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [slides]);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-12 md:py-10">
      {/* Header */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center gap-3 mb-6">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-black">
              EventIQ Features
            </h1>
            <p className="text-gray-600 text-lg mt-2 max-w-2xl">
              Explore the powerful features that make event management effortless
            </p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Left Panel - Feature Info */}
        <div>
          <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border border-gray-200">
            {/* Slide Counter */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${currentSlideData.gradient}`}>
                  {currentSlideData.icon}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Feature {currentSlide + 1} of {slides.length}</div>
                  <div className="text-lg font-bold text-gray-900">{currentSlideData.title}</div>
                </div>
              </div>
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label={isAutoPlaying ? "Pause auto-play" : "Start auto-play"}
              >
                {isAutoPlaying ? (
                  <PauseIcon className="w-5 h-5 text-gray-700" />
                ) : (
                  <PlayIcon className="w-5 h-5 text-gray-700" />
                )}
              </button>
            </div>

            {/* Feature Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                <span className="font-medium text-gray-800">{currentSlideData.highlight}</span>
              </div>

              <p className="text-gray-600 text-lg leading-relaxed">
                {currentSlideData.description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4">
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50">
                  <div className="text-2xl font-bold text-green-600">500+</div>
                  <div className="text-sm text-green-600 font-medium">Events/Day</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50">
                  <div className="text-2xl font-bold text-green-600">98%</div>
                  <div className="text-sm text-green-600 font-medium">Satisfaction</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50">
                  <div className="text-2xl font-bold text-green-600">50K+</div>
                  <div className="text-sm text-green-600 font-medium">Users</div>
                </div>
              </div>

              {/* Feature Points */}
              <div className="space-y-3 pt-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircleIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Easy event creation and management</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircleIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Real-time analytics and tracking</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircleIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Secure data and privacy control</span>
                </div>
              </div>

              {/* CTA Button */}
              <button className="w-full mt-6 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 group">
                <span className="text-lg">{currentSlideData.cta || "Get Started"}</span>
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Slide Dots Navigation */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 ${index === currentSlide ? 'w-10 h-2 rounded-full bg-gradient-to-r from-green-600 to-emerald-600' : 'w-2 h-2 rounded-full bg-gray-300 hover:bg-gray-400'}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Quick Stats */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-2 gap-4">
            {[
              { icon: <BoltIcon className="w-5 h-5" />, label: "Fast Setup", value: "30s" },
              { icon: <ShieldCheckIcon className="w-5 h-5" />, label: "Secure", value: "100%" },
              { icon: <QrCodeIcon className="w-5 h-5" />, label: "QR Share", value: "Instant" },
              { icon: <MapPinIcon className="w-5 h-5" />, label: "Locations", value: "Global" },
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-4 rounded-xl border border-gray-200 hover:border-green-300 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500">
                    <div className="text-white">
                      {item.icon}
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{item.label}</div>
                    <div className="text-sm text-green-600 font-medium">{item.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Image Slider */}
        <div>
          <div className="relative">
            {/* Main Image Display */}
            <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-xl overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center h-[600px]">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                </div>
              ) : (
                <div className="relative">
                  <img 
                    src={currentSlideData.imageUrl} 
                    alt={`EventIQ Screenshot - ${currentSlideData.title}`}
                    className="w-full h-auto max-h-[600px] object-contain"
                    loading="lazy"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent"></div>
                </div>
              )}

              {/* Navigation Controls */}
              <div className="absolute inset-0 flex items-center justify-between px-4">
                <button
                  onClick={prevSlide}
                  className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-white transition-all active:scale-95 group"
                  aria-label="Previous slide"
                >
                  <ChevronLeftIcon className="w-6 h-6 text-gray-700 group-hover:text-green-600" />
                </button>
                <button
                  onClick={nextSlide}
                  className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-white transition-all active:scale-95 group"
                  aria-label="Next slide"
                >
                  <ChevronRightIcon className="w-6 h-6 text-gray-700 group-hover:text-green-600" />
                </button>
              </div>

              {/* Slide Indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                <div className="px-4 py-2 rounded-full bg-white/90 backdrop-blur-sm shadow-lg">
                  <span className="text-sm font-medium text-gray-700">
                    {currentSlide + 1} / {slides.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Thumbnail Navigation */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">All Screenshots</h3>
                <div className="text-sm text-green-600 font-medium">
                  Click to preview
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    onClick={() => goToSlide(index)}
                    className={`relative overflow-hidden rounded-lg border-2 transition-all duration-300 group ${
                      currentSlide === index 
                        ? 'border-green-600 ring-2 ring-green-200 shadow-md' 
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="aspect-[9/16] overflow-hidden rounded">
                      <img 
                        src={slide.imageUrl} 
                        alt={`Thumbnail ${slide.id}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      {currentSlide === index && (
                        <div className="absolute inset-0 bg-green-500/20"></div>
                      )}
                    </div>
                    {currentSlide === index && (
                      <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-green-600 ring-2 ring-white"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* App Store Badge */}
            <div className="mt-8">
              <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-6 text-center">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <DevicePhoneMobileIcon className="w-10 h-10 text-white" />
                  <div className="text-left">
                    <div className="text-white text-sm">Available now on</div>
                    <div className="text-white text-2xl font-bold">Google Play</div>
                  </div>
                </div>
                <button className="w-full px-6 py-3 bg-white text-green-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                  Download Now
                </button>
                <p className="text-white/80 text-sm mt-4">
                  Join 50,000+ event organizers
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="mt-16 pt-8 border-t border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Why Choose EventIQ?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-2xl border border-green-100">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
              <BoltIcon className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">Lightning Fast</h3>
            <p className="text-gray-600">Create and manage events in seconds with our intuitive interface.</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-2xl border border-green-100">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
              <ShieldCheckIcon className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">Secure & Private</h3>
            <p className="text-gray-600">Your data is protected with enterprise-grade security and privacy controls.</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-2xl border border-green-100">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
              <SparklesIcon className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">Smart Features</h3>
            <p className="text-gray-600">AI-powered insights and automation to make event management effortless.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppScreenshotSlider;